// src/pages/Dashboard/Dashboard.jsx
import React, { useMemo, useState, useRef, useLayoutEffect, useEffect } from "react";
import {
  Box,
  Typography,
  useTheme,
  Stack,
  useMediaQuery,
  Button,
  TextField,
} from "@mui/material";
import { DataGrid, esES } from "@mui/x-data-grid";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { getTurnosByDrivers, getMisTurnos } from "../../services/turnoService";
import { getDriversByBoss } from "../../services/driverService";

/* ===== Paletas coherentes con Turnos/TurnosLiquidados ===== */
const PALETTE_DARK = {
  cardBg: "#1E293B",
  cardBorder: "#334155",
  text: "#F1F5F9",
  label: "#94A3B8",
  title: "#E2E8F0",
  accent: "#38BDF8",
  btn: "#3B82F6",
  btnHover: "#2563EB",
};
const PALETTE_LIGHT = {
  cardBg: "#ECECEC",
  cardBorder: "#E5E7EB",
  text: "#1E293B",
  label: "#475569",
  title: "#111827",
  accent: "#38BDF8",
  btn: "#2563EB",
  btnHover: "#1D4ED8",
};

/* ===== Widget ===== */
function StatCard({ icon, label, value, palette, highlight = false, isMobile = false }) {
  return (
    <Box
      sx={{
        p: isMobile ? 1.75 : 2,
        borderRadius: "12px",
        backgroundColor: palette.cardBg,
        border: `1px solid ${highlight ? palette.accent : palette.cardBorder}`,
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: "center",
        justifyContent: isMobile ? "center" : "flex-start",
        gap: isMobile ? 1 : 1.25,
        minHeight: 76,
        boxShadow:
          palette === PALETTE_LIGHT
            ? "0 6px 18px rgba(2,6,23,0.08)"
            : "0 2px 6px rgba(0,0,0,0.20)",
      }}
    >
      <Box sx={{ opacity: 0.95, display: "grid", placeItems: "center" }}>
        {icon}
      </Box>
      <Box sx={{ flex: 1, textAlign: isMobile ? "center" : "left" }}>
        <Typography
          variant="caption"
          sx={{
            color: palette.label,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            fontWeight: 600,
          }}
        >
          {label}
        </Typography>
        <Typography variant="h6" sx={{ color: palette.text, fontWeight: 800 }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

/* ===== Card ===== */
function Card({ title, palette, children, sx: sxProp = {} }) {
  return (
    <Box
      sx={{
        backgroundColor: palette.cardBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: "14px",
        p: 1.5,
        height: "100%",
        minHeight: { xs: "auto", md: 0 },
        display: "flex",
        flexDirection: "column",
        boxShadow:
          palette === PALETTE_LIGHT
            ? "0 8px 22px rgba(2,6,23,0.08)"
            : "0 3px 10px rgba(0,0,0,0.24)",
        overflow: "hidden",
        width: "100%",
        ...sxProp,
      }}
    >
      <Typography variant="h6" sx={{ color: palette.title, fontWeight: 800, mb: 1 }}>
        {title}
      </Typography>
      <Box
        sx={{
          flex: 1,
          minHeight: { xs: "auto", md: 0 },
          width: "100%",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

/* ===== Estilo DataGrid ===== */
const dgSx = (palette) => ({
  "& .MuiDataGrid-root": {
    border: "none",
    color: palette.text,
    backgroundColor: "transparent",
    height: "100%",
    width: "100%",
  },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "transparent",
    borderBottom: `1px solid ${palette.cardBorder}`,
    color: palette.label,
    fontWeight: 700,
  },
  "& .MuiDataGrid-cell": { borderBottom: "none" },
  "& .MuiDataGrid-virtualScroller": { backgroundColor: "transparent" },
  "& .MuiDataGrid-footerContainer": {
    borderTop: `1px solid ${palette.cardBorder}`,
    backgroundColor: "transparent",
  },
  "& .MuiDataGrid-overlay": {
    backgroundColor: "transparent",
    color: palette.label,
  },
});
const mobileGridSx = {
  "& .MuiDataGrid-root": { height: "auto !important" },
  "& .MuiDataGrid-main": { overflow: "visible" },
  "& .MuiDataGrid-virtualScroller": { overflow: "visible !important" },
};

/* ===== Hook: calcula pageSize para que no haya scroll vertical ===== */
function useAutoPageSize(rowHeight = 52, headerHeight = 48, footerHeight = 56, minRows = 1) {
  const ref = useRef(null);
  const [pageSize, setPageSize] = useState(Math.max(minRows, 5));

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const calc = () => {
      const totalH = el.clientHeight; // alto disponible dentro del card para el grid
      const rowsH = Math.max(0, totalH - headerHeight - footerHeight);
      const n = Math.max(minRows, Math.floor(rowsH / rowHeight));
      setPageSize(n);
    };

    const ro = new ResizeObserver(calc);
    ro.observe(el);
    calc();
    return () => ro.disconnect();
  }, [rowHeight, headerHeight, footerHeight]);

  return { containerRef: ref, pageSize };
}

const Dashboard = () => {
  const theme = useTheme();
  const palette = useMemo(
    () => (theme.palette.mode === "dark" ? PALETTE_DARK : PALETTE_LIGHT),
    [theme.palette.mode]
  );
  const isMobile = useMediaQuery("(max-width:900px)");
  const { userId, type: userType } = useSelector((s) => s.user);
  const navigate = useNavigate();
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const monthStart = useMemo(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const formatDate = (d) => d.toISOString().split("T")[0];
  const [dateRange, setDateRange] = useState({
    desde: formatDate(monthStart),
    hasta: formatDate(today),
  });
  const desdeRef = useRef(null);
  const hastaRef = useRef(null);

  const [closures, setClosures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const closuresVisibility = useMemo(
    () =>
      isMobile
        ? {
            km: false,
            tarjeta: false,
            credito: false,
            gasolina: false,
            vehicle: false,
            license: false,
          }
        : {},
    [isMobile]
  );
  const dateFieldSx = useMemo(
    () => ({
      minWidth: 160,
      "& .MuiOutlinedInput-notchedOutline": { borderColor: palette.cardBorder },
      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: palette.accent },
      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: palette.accent },
      "& .MuiInputBase-input": { color: palette.text },
      "& .MuiInputLabel-root": { color: palette.label },
      "& .MuiInputLabel-root.Mui-focused": { color: palette.text },
      "& input::-webkit-calendar-picker-indicator": {
        filter: theme.palette.mode === "dark" ? "invert(1)" : "invert(0)",
      },
    }),
    [palette.cardBorder, palette.accent, palette.label, palette.text, theme.palette.mode]
  );

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        let closuresData = [];
        if (userType === "Boss") {
          const drv = await getDriversByBoss();
          const driverIds = (drv || []).map((d) => d._id);
          closuresData = driverIds.length
            ? await getTurnosByDrivers(driverIds, userId, {
                startDate: dateRange.desde,
                endDate: dateRange.hasta,
              })
            : [];
        } else {
          closuresData = await getMisTurnos({
            userId,
            startDate: dateRange.desde,
            endDate: dateRange.hasta,
          });
        }

        const normalized = (closuresData || []).map((c, idx) => {
          const factServicios = Number(c.facturacionServicios || 0);
          const factResRaw = Number(c.facturacionResuelta);
          const facturacionResuelta =
            Number.isFinite(factResRaw) && (factResRaw !== 0 || factServicios === 0)
              ? factResRaw
              : factServicios ||
                Number(c.facturacionTotal || 0) ||
                Number(c.endIncome || 0) - Number(c.startIncome || 0);
          const km =
            Number.isFinite(c.endKm) && Number.isFinite(c.startKm)
              ? Number(c.endKm) - Number(c.startKm)
              : 0;
          return {
            id: c._id || `cc_${idx}`,
            rawId: c._id || null,
            driverName: c.driver?.name || c.driverName || "—",
            facturacion: facturacionResuelta,
            tarjeta: Number(c.cardIncome || 0),
            credito: Number(c.creditIncome || 0),
            gasolina: Number(c.gas || 0),
            km,
            license: c.license || "—",
            vehicle: c.vehicleNumber || "—",
            date: c.date ? new Date(c.date) : null,
          };
        });
        setClosures(normalized);
      } catch (err) {
        console.error("Error cargando dashboard:", err);
        setError(err?.message || "No se pudo cargar el dashboard");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [userId, userType, dateRange.desde, dateRange.hasta]);

  /* ===== Columnas ===== */
  const columnasCierres = [
    { field: "driverName", headerName: "Conductor", flex: 1 },
    {
      field: "facturacion",
      headerName: "Facturación (€)",
      flex: 1,
      valueGetter: (p) => Number(p.value || 0).toFixed(2),
    },
    {
      field: "tarjeta",
      headerName: "Cobro Tarjeta (€)",
      flex: 1,
      valueGetter: (p) => Number(p.value || 0).toFixed(2),
    },
    {
      field: "credito",
      headerName: "Servicio Crédito (€)",
      flex: 1,
      valueGetter: (p) => Number(p.value || 0).toFixed(2),
    },
    {
      field: "gasolina",
      headerName: "Gasolina (€)",
      flex: 1,
      valueGetter: (p) => Number(p.value || 0).toFixed(2),
    },
    { field: "vehicle", headerName: "Vehículo", flex: 1 },
    {
      field: "acciones",
      headerName: "Acciones",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          onClick={() => navigate(`/turnos/${params.row.rawId || params.row.id}`)}
          sx={{
            backgroundColor: palette.btn,
            color: "white",
            fontWeight: 700,
            textTransform: "none",
            "&:hover": { backgroundColor: palette.btnHover },
          }}
        >
          Ver detalles
        </Button>
      ),
    },
  ];
  const columnasMantenimientos = [
    { field: "vehiculo", headerName: "Vehículo", flex: 1 },
    { field: "mantenimiento", headerName: "Mantenimiento", flex: 1 },
    { field: "fecha", headerName: "Fecha", flex: 1 },
  ];
  const columnasConductor = [
    { field: "nombre", headerName: "Nombre", flex: 1 },
    { field: "kmTotales", headerName: "Km Totales", flex: 1 },
    { field: "facturacion", headerName: "Facturación (€)", flex: 1 },
    { field: "diasTrabajados", headerName: "Días Trabajados", flex: 1 },
    { field: "tarjeta", headerName: "Cobros Tarjeta (€)", flex: 1 },
    { field: "credito", headerName: "Servicios Crédito (€)", flex: 1 },
  ];
  const conductorVisibility = useMemo(
    () =>
      isMobile
        ? {
            tarjeta: false,
            credito: false,
            diasTrabajados: false,
          }
        : {},
    [isMobile]
  );

  /* ===== Auto page size solo para la tarjeta IZQUIERDA ===== */
  const { containerRef: leftGridBoxRef, pageSize: leftPageSize } = useAutoPageSize(
    52,
    48,
    56,
    isMobile ? 5 : 5
  );
  const [leftPage, setLeftPage] = useState(0);

  /* ===== Métricas ===== */
  const totalCierres = closures.length;
  const facturacionTotal = closures
    .reduce((sum, c) => sum + Number(c.facturacion || 0), 0)
    .toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const kmTotales = closures
    .reduce((sum, c) => sum + Number(c.km || 0), 0)
    .toLocaleString("es-ES");

  const ultimosCierres = [...closures]
    .sort((a, b) => (b.date || 0) - (a.date || 0))
    .slice(0, 50);

  const agregadosPorConductor = useMemo(() => {
    const agg = new Map();
    (closures || []).forEach((c) => {
      const key = c.driverName || "—";
      const cur = agg.get(key) || {
        nombre: key,
        kmTotales: 0,
        facturacion: 0,
        diasTrabajados: 0,
        tarjeta: 0,
        credito: 0,
      };
      cur.kmTotales += Number(c.km || 0);
      cur.facturacion += Number(c.facturacion || 0);
      cur.tarjeta += Number(c.tarjeta || 0);
      cur.credito += Number(c.credito || 0);
      cur.diasTrabajados += 1;
      agg.set(key, cur);
    });

    return Array.from(agg.values()).map((item, idx) => ({
      id: `drv_${idx}`,
      ...item,
      kmTotales: item.kmTotales.toFixed(0),
      facturacion: item.facturacion.toFixed(2),
      tarjeta: item.tarjeta.toFixed(2),
      credito: item.credito.toFixed(2),
    }));
  }, [closures]);

  const mantenimientos = [];

  return (
    <Box
      sx={{
        m: { xs: "12px", md: "20px" },
        display: "flex",
        flexDirection: "column",
        height: { xs: "auto", md: "100vh" },
        minHeight: { xs: "100vh", md: 0 },
        overflowX: "hidden",
        overflowY: { xs: "auto", md: "hidden" },
        gap: { xs: 1.5, md: 0 },
      }}
    >
      <Header title="Panel de Control" subtitle="Bienvenido a tu panel" />
      {error && (
        <Typography sx={{ color: palette.accent, mt: 1 }}>
          {error}
        </Typography>
      )}

      {/* Widgets */}
      <Box
        sx={{
          mt: 1,
          mb: 2,
          display: "flex",
          gap: 2,
          width: "100%",
          justifyContent: "space-between",
          flexWrap: "nowrap",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <StatCard icon={<TrendingUpIcon />} label="Total Cierres" value={totalCierres} palette={palette} isMobile={isMobile} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <StatCard icon={<AttachMoneyIcon />} label="Facturación" value={`€${facturacionTotal}`} palette={palette} highlight isMobile={isMobile} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <StatCard icon={<DirectionsCarIcon />} label="Km Recorridos" value={`${kmTotales} km`} palette={palette} isMobile={isMobile} />
        </Box>
      </Box>

      {/* Filtro de fechas */}
      <Stack direction="column" alignItems="flex-start" gap={0.75} sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ color: palette.text, whiteSpace: "nowrap" }}>Rango de fechas:</Typography>
        <Stack direction="row" gap={1.25} flexWrap="wrap">
          <TextField
            label="Desde"
            type="date"
            size="small"
            value={dateRange.desde}
            onChange={(e) => setDateRange((prev) => ({ ...prev, desde: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            inputRef={desdeRef}
            inputProps={{
              onMouseDown: (e) => e.target?.showPicker?.(),
              onTouchStart: (e) => e.target?.showPicker?.(),
            }}
            sx={dateFieldSx}
          />
          <TextField
            label="Hasta"
            type="date"
            size="small"
            value={dateRange.hasta}
            onChange={(e) => setDateRange((prev) => ({ ...prev, hasta: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            inputRef={hastaRef}
            inputProps={{
              onMouseDown: (e) => e.target?.showPicker?.(),
              onTouchStart: (e) => e.target?.showPicker?.(),
            }}
            sx={dateFieldSx}
          />
        </Stack>
      </Stack>

      {/* Contenido principal — 50/50 */}
      <Box
        sx={{
          flex: "1 1 auto",
          minHeight: { xs: "auto", lg: 0 },
          height: { xs: "auto", lg: "calc(100vh - 200px)" },
          maxHeight: { xs: "auto", lg: "calc(100vh - 200px)" },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
          columnGap: 2,
          rowGap: { xs: 2, lg: 0 },
          alignItems: "stretch",
          justifyItems: "stretch",
          overflowX: "hidden",
          overflowY: { xs: "visible", lg: "hidden" },
        }}
      >
        {/* IZQUIERDA — se estira y ajusta pageSize para no tener scroll */}
        <Box
          sx={{
            minHeight: { xs: "auto", lg: 0 },
            height: isMobile ? "auto" : "100%",
            display: "flex",
            alignItems: "stretch",
            overflow: { xs: "visible", lg: "hidden" },
          }}
        >
          <Card title="Últimos Cierres Contables" palette={palette} sx={{ flex: 1 }}>
            {/* ⬇️ Este Box define el área exacta que debe ocupar el grid */}
            <Box
              ref={isMobile ? null : leftGridBoxRef}
              sx={{
                ...dgSx(palette),
                display: "flex",
                flexDirection: "column",
                height: isMobile ? "auto" : "100%",
                minHeight: isMobile ? "auto" : "100%",
                overflow: isMobile ? "visible" : "auto",
              }}
            >
              <DataGrid
                autoHeight={isMobile}
                rows={ultimosCierres}
                columns={columnasCierres}
                columnVisibilityModel={closuresVisibility}
                paginationModel={{ page: leftPage, pageSize: leftPageSize }}
                onPaginationModelChange={(m) => setLeftPage(m.page)}
                pageSizeOptions={[leftPageSize]}
                loading={loading}
                localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                rowHeight={52}
                columnHeaderHeight={48}
                sx={{
                  height: isMobile ? "auto" : "100%",
                  width: "100%",
                  ...(isMobile ? mobileGridSx : {}),
                }}
                onRowClick={(params) => {
                  if (isMobile && (params.row.rawId || params.row.id)) {
                    navigate(`/turnos/${params.row.rawId || params.row.id}`);
                  }
                }}
              />
            </Box>
          </Card>
        </Box>

        {/* DERECHA — dos tarjetas con gap propio */}
        <Box
          sx={{
            minHeight: { xs: "auto", lg: 0 },
            height: "100%",
            display: "grid",
            gridTemplateRows: { xs: "auto auto", lg: "1fr 1fr" },
            rowGap: 2,
            alignItems: "stretch",
          }}
        >
          <Card title="Próximos Mantenimientos" palette={palette} sx={{ flex: 1 }}>
            <Box
              sx={{
                ...dgSx(palette),
                flex: 1,
                minHeight: { xs: "auto", lg: 0 },
                display: "flex",
                flexDirection: "column",
                height: isMobile ? "auto" : "100%",
                overflow: isMobile ? "visible" : "hidden",
              }}
            >
              <DataGrid
                autoHeight={isMobile}
                rows={mantenimientos}
                columns={columnasMantenimientos}
                pageSizeOptions={[5, 10]}
                initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
                localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                rowHeight={52}
                columnHeaderHeight={48}
                sx={{
                  height: isMobile ? "auto" : "100%",
                  width: "100%",
                  ...(isMobile ? mobileGridSx : {}),
                }}
                slots={{
                  noRowsOverlay: () => (
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: palette.label,
                        p: 2,
                      }}
                    >
                      No hay mantenimientos registrados.
                    </Box>
                  ),
                }}
              />
            </Box>
          </Card>

          <Card title="Resumen por Conductor" palette={palette} sx={{ flex: 1 }}>
            <Box
              sx={{
                ...dgSx(palette),
                flex: 1,
                minHeight: { xs: "auto", lg: 0 },
                display: "flex",
                flexDirection: "column",
                height: isMobile ? "auto" : "100%",
                overflow: isMobile ? "visible" : "hidden",
              }}
            >
              <DataGrid
                autoHeight={isMobile}
                rows={agregadosPorConductor}
                columns={columnasConductor}
                columnVisibilityModel={conductorVisibility}
                pageSizeOptions={[5, 10]}
                initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
                localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                rowHeight={52}
                columnHeaderHeight={48}
                sx={{
                  height: isMobile ? "auto" : "100%",
                  width: "100%",
                  ...(isMobile ? mobileGridSx : {}),
                }}
                slots={{
                  noRowsOverlay: () => (
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: palette.label,
                        p: 2,
                      }}
                    >
                      No hay datos en el rango seleccionado.
                    </Box>
                  ),
                }}
              />
            </Box>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
